import { supabase } from "@/services/supabaseClient";

export const INSTITUTION_CLINICIAN_EVIDENCE_BUCKET = "institution-clinician-evidence";

const CLINICIAN_EVIDENCE_MIME_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
]);

const CLINICIAN_IMAGE_MIME_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);

function evidenceFileExtension(file) {
  const nameExtension = String(file?.name || "").split(".").pop().toLowerCase();
  if (/^[a-z0-9]{1,8}$/.test(nameExtension)) return nameExtension;
  return String(file?.type || "").split("/").pop().replace(/[^a-z0-9]/gi, "").toLowerCase() || "bin";
}

function evidenceObjectName(kind, file) {
  const suffix = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${kind}/${suffix}.${evidenceFileExtension(file)}`;
}

function validateInstitutionClinicianEvidence(file, { imageOnly = false } = {}) {
  if (!file || typeof file !== "object") throw new Error("Select a file to upload.");
  const allowed = imageOnly ? CLINICIAN_IMAGE_MIME_TYPES : CLINICIAN_EVIDENCE_MIME_TYPES;
  if (!allowed.has(String(file.type || "").toLowerCase())) {
    throw new Error(imageOnly ? "Choose a JPG, PNG or WEBP image." : "Choose a PDF, JPG, PNG or WEBP file.");
  }
  if (Number(file.size || 0) > 10 * 1024 * 1024) throw new Error("Choose a file up to 10 MB.");
}

export async function uploadInstitutionClinicianEvidence({ institutionId, file, kind, imageOnly = false }) {
  const parentId = normalize(institutionId);
  if (!parentId) throw new Error("Active parent Institution required.");
  validateInstitutionClinicianEvidence(file, { imageOnly });
  const path = `${parentId}/${evidenceObjectName(kind, file)}`;
  const { error } = await supabase.storage.from(INSTITUTION_CLINICIAN_EVIDENCE_BUCKET).upload(path, file, {
    cacheControl: "3600",
    contentType: file.type,
    upsert: false,
  });
  if (error) throw error;
  return {
    path,
    fileName: String(file.name || "Evidence file").slice(0, 255),
    mimeType: String(file.type || "").toLowerCase(),
    uploadedAt: new Date().toISOString(),
  };
}

export async function removeInstitutionClinicianEvidence(path) {
  const source = normalize(path);
  if (!source) return;
  const { error } = await supabase.storage.from(INSTITUTION_CLINICIAN_EVIDENCE_BUCKET).remove([source]);
  if (error) throw error;
}

async function signedInstitutionClinicianAsset(path, bucket = INSTITUTION_CLINICIAN_EVIDENCE_BUCKET) {
  const source = normalize(path);
  if (!source) return "";
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(source, 60 * 15);
  if (error) throw error;
  return data?.signedUrl || "";
}

async function hydrateInstitutionClinician(clinician, institutionId) {
  const imagePath = normalize(clinician?.imagePath);
  const imageBucket = normalize(clinician?.imageBucket);
  const imageUrl = normalize(clinician?.imageUrl);
  let hydratedImageUrl = imageUrl;
  if (clinician?.clinicianProfileId && clinician?.id && institutionId) {
    try {
      const protectedMedia = await loadInstitutionClinicianMediaDisplay({ institutionId, rosterId: clinician.id });
      hydratedImageUrl = protectedMedia.avatarDisplayUrl;
    } catch {
      hydratedImageUrl = "";
    }
  } else if (!hydratedImageUrl && imagePath && imageBucket) {
    try {
      hydratedImageUrl = await signedInstitutionClinicianAsset(imagePath, imageBucket);
    } catch {
      hydratedImageUrl = "";
    }
  }
  const education = await Promise.all((Array.isArray(clinician?.education) ? clinician.education : []).map(async (item) => {
    const evidence = item?.evidence && typeof item.evidence === "object" ? item.evidence : null;
    if (!evidence?.path || !String(evidence.mimeType || "").startsWith("image/")) return item;
    try {
      return { ...item, evidence: { ...evidence, previewUrl: await signedInstitutionClinicianAsset(evidence.path) } };
    } catch {
      return item;
    }
  }));
  return { ...clinician, imagePath: hydratedImageUrl || "", education };
}

export async function hydrateInstitutionClinicianSnapshot(snapshot, institutionId = "") {
  const clinicians = await Promise.all((Array.isArray(snapshot?.clinicians) ? snapshot.clinicians : []).map((clinician) => hydrateInstitutionClinician(clinician, institutionId)));
  return { ...(snapshot || {}), clinicians };
}

export const INSTITUTIONAL_ROLES = new Set([
  "admin",
  "super_admin",
  "moh_admin",
  "institution_admin",
  "facility_admin",
]);

export const MEMBER_ADMIN_ROLES = new Set([
  "owner",
  "admin",
  "moh_admin",
  "institution_admin",
  "facility_admin",
]);

function normalize(value) {
  return String(value || "").trim();
}

function normalizeRole(value) {
  const role = normalize(value).toLowerCase();
  if (role === "moh") return "moh_admin";
  if (role === "institution") return "institution_admin";
  return role;
}

function unique(values) {
  return Array.from(new Set(values.filter(Boolean)));
}

function isMissingTable(error) {
  return error?.code === "42P01" || /does not exist/i.test(String(error?.message || ""));
}

function isMissingColumn(error) {
  return error?.code === "42703" || /column .* does not exist/i.test(String(error?.message || ""));
}

function isAccessDenied(error) {
  return error?.code === "42501" || error?.status === 403 || /permission denied|not authorized/i.test(String(error?.message || ""));
}

async function safeList(builder, { allowMissing = true, allowAccessDenied = false } = {}) {
  try {
    const { data, error, count } = await builder;
    if (error) throw error;
    return { rows: Array.isArray(data) ? data : [], count: Number.isFinite(count) ? count : null };
  } catch (error) {
    if (allowMissing && (isMissingTable(error) || isMissingColumn(error))) {
      return { rows: [], count: null, missing: true, error };
    }
    if (allowAccessDenied && isAccessDenied(error)) {
      return { rows: [], count: null, denied: true, error };
    }
    throw error;
  }
}

async function safeCount(builder, { allowMissing = true } = {}) {
  try {
    const { count, error } = await builder;
    if (error) throw error;
    return Number.isFinite(count) ? count : 0;
  } catch (error) {
    if (allowMissing && (isMissingTable(error) || isMissingColumn(error))) return null;
    throw error;
  }
}

async function safeRpc(name, args = {}, { allowMissing = true, allowAccessDenied = false } = {}) {
  try {
    const { data, error } = await supabase.rpc(name, args);
    if (error) throw error;
    return { data };
  } catch (error) {
    const missingRpc = error?.code === "42883" || error?.code === "PGRST202" || /could not find the function/i.test(String(error?.message || ""));
    if (allowMissing && (isMissingTable(error) || isMissingColumn(error) || missingRpc)) {
      return { data: null, missing: true, error };
    }
    if (allowAccessDenied && isAccessDenied(error)) {
      return { data: null, denied: true, error };
    }
    const workspaceError = new Error(error?.message || `Institution workspace request failed: ${name}.`);
    workspaceError.code = error?.code || "";
    workspaceError.details = error?.details || "";
    workspaceError.hint = error?.hint || "";
    workspaceError.workspaceOperation = name;
    throw workspaceError;
  }
}

function mohExploreSetupForInstitution(entry, institutionId) {
  const targetInstitutionId = normalize(institutionId);
  const setups = entry?.mohExploreSetups && typeof entry.mohExploreSetups === "object"
    ? entry.mohExploreSetups
    : {};
  const setupStatus = normalize(setups[targetInstitutionId]).toLowerCase();
  if (!targetInstitutionId || !setupStatus) return null;
  return {
    institutionId: targetInstitutionId,
    setupStatus,
    canResume: true,
  };
}

async function readMoHExploreSetupForInstitution(institutionId) {
  // The caller-scoped entry RPC distinguishes Explore-governed workspaces from
  // normal Institution workspaces. The latter must never call the MoH setup RPC.
  const entryResult = await safeRpc("get_moh_workspace_entry_state", {}, {
    allowAccessDenied: true,
  }).catch(() => ({ data: null }));
  return mohExploreSetupForInstitution(entryResult?.data, institutionId);
}

export function getMemberStatus(member) {
  const explicit = normalize(member?.status).toLowerCase();
  if (explicit) return explicit;
  return member?.user_id ? "active" : "invited";
}

export function canUseInstitutionWorkspace({ userRoles = [], memberships = [] } = {}) {
  const roleAllowed = userRoles.some((role) => INSTITUTIONAL_ROLES.has(normalizeRole(role)));
  const memberAllowed = memberships.some((member) => MEMBER_ADMIN_ROLES.has(normalizeRole(member?.role)));
  return roleAllowed || memberAllowed || memberships.some((member) => member?.institution_id);
}

export function canManageInstitutionServices({ userRoles = [], memberships = [], institutionId = "" } = {}) {
  const parentId = normalize(institutionId);
  const hasGlobalRole = userRoles.some((role) => ["admin", "super_admin"].includes(normalizeRole(role)));
  return memberships.some((member) => {
    if (normalize(member?.institution_id) !== parentId || getMemberStatus(member) !== "active") return false;
    const role = normalizeRole(member?.role);
    return ["owner", "admin", "moh_admin", "institution_admin"].includes(role) || (role === "super_admin" && hasGlobalRole);
  });
}

export async function loadInstitutionWorkspace({ institutionId = "", loadMode = "full" } = {}) {
  const auth = await supabase.auth.getUser();
  const user = auth?.data?.user || null;
  if (!user?.id) return { status: "signed_out" };

  const requestedInstitutionId = normalize(institutionId);
  const scopedOperationalMode = loadMode === "follow-ups" || loadMode === "regional-health";
  const followupEntryResult = scopedOperationalMode && requestedInstitutionId
    ? safeRpc("institution_synthetic_contact_followup_workspace_snapshot", {
      target_institution: requestedInstitutionId,
    }, { allowAccessDenied: true })
    : Promise.resolve({ data: null });
  const [{ rows: roleRows }, { rows: memberRows }, globalInstitutionRoleResult, followupEntry] = await Promise.all([
    safeList(supabase.from("user_roles").select("role").eq("user_id", user.id), {
      allowAccessDenied: true,
    }),
    safeList(
      supabase
        .from("institution_members")
        .select("*")
        .or(`user_id.eq.${user.id},email.eq.${String(user.email || "").toLowerCase()}`)
        .order("created_at", { ascending: false }),
      { allowAccessDenied: true },
    ),
    safeRpc("has_global_institution_role"),
    followupEntryResult,
  ]);

  const userRoles = unique(roleRows.map((row) => normalizeRole(row?.role)));
  const isSuperAdmin =
    globalInstitutionRoleResult?.data === true ||
    userRoles.includes("admin") ||
    userRoles.includes("super_admin");
  const hasScopedFollowupAccess = scopedOperationalMode && requestedInstitutionId && Array.isArray(followupEntry?.data?.assignments) && followupEntry.data.assignments.length > 0;
  const canAccess = isSuperAdmin || canUseInstitutionWorkspace({ userRoles, memberships: memberRows }) || hasScopedFollowupAccess;
  if (!canAccess) {
    return { status: "forbidden", user, userRoles, memberships: memberRows };
  }

  const activeMemberInstitutionIds = unique(
    memberRows
      .filter((member) => getMemberStatus(member) === "active")
      .map((member) => member?.institution_id),
  );
  if (hasScopedFollowupAccess) activeMemberInstitutionIds.push(requestedInstitutionId);
  // Institution surfaces are customer-governance workspaces. A platform-wide
  // role can permit the route shell, but it must never expand the Institution
  // data set beyond this user's active, linked memberships.
  const institutionIds = activeMemberInstitutionIds;
  const institutionQuery = supabase.from("medical_institutions").select("*").order("created_at", { ascending: false });
  const { rows: institutions } = institutionIds.length
    ? await safeList(institutionQuery.in("id", institutionIds))
    : { rows: [] };

  const activeInstitution =
    institutions.find((institution) => institution?.id === requestedInstitutionId) ||
    institutions.find((institution) => activeMemberInstitutionIds.includes(institution?.id)) ||
    institutions[0] ||
    null;
  if (!activeInstitution?.id) {
    return {
      status: "empty",
      user,
      userRoles,
      memberships: memberRows,
      institutions,
      activeInstitution: null,
      requiresSetup: ["Institution assignment"],
    };
  }

  const scopedMembers = memberRows.filter((row) => row?.institution_id === activeInstitution.id);
  const scopedRole = isSuperAdmin ? "super_admin" : hasScopedFollowupAccess ? "scoped_followup_reviewer" : normalizeRole(scopedMembers[0]?.role);
  const setup = [];

  // Home needs the caller's governed Institution context, active Members and
  // MoH setup state only when this is an Explore-governed workspace. It deliberately owns its own authorised network
  // snapshot for recency, so waiting here for every independent product
  // snapshot (clinicians, services, patients and surveillance) only delays a
  // newly provisioned, otherwise empty workspace.
  if (loadMode === "home" || loadMode === "regional-health" || loadMode === "follow-ups") {
    const [memberResult, mohSetup] = await Promise.all([
      safeList(
        supabase
          .from("institution_members")
          .select("*")
          .eq("institution_id", activeInstitution.id)
          .order("created_at", { ascending: false }),
        { allowAccessDenied: true },
      ),
      readMoHExploreSetupForInstitution(activeInstitution.id),
    ]);

    const memberUserIds = unique(memberResult.rows.map((row) => row?.user_id));
    let memberProfiles = [];
    if (memberUserIds.length) {
      try {
        const result = await safeList(
          supabase.from("profiles").select("user_id,avatar_url").in("user_id", memberUserIds),
        );
        memberProfiles = result.rows;
      } catch {
        // The row remains usable with initials when a shared profile image is not readable.
        memberProfiles = [];
      }
    }
    const memberProfileByUserId = new Map(memberProfiles.map((row) => [row.user_id, row]));
    const hydratedMembers = memberResult.rows.map((member) => {
      const profile = memberProfileByUserId.get(member?.user_id);
      return {
        ...member,
        avatar_url: member?.avatar_url || profile?.avatar_url || "",
      };
    });

    return {
      status: "ready",
      user,
      userRoles,
      scopedRole,
      memberships: memberRows,
      institutions,
      activeInstitution,
      members: hydratedMembers,
      linkedFacilities: [],
      linkedClinicians: [],
      clinicianFacilities: [],
      services: [],
      serviceFacilities: [],
      serviceSpecialties: [],
      patients: [],
      patientFacilities: [],
      patientHealthProfileAccess: false,
      patientContractAvailable: false,
      canManageServices: canManageInstitutionServices({ userRoles, memberships: memberRows, institutionId: activeInstitution.id }),
      documents: [],
      surveillance: {
        programmes: [],
        facilities: [],
        assignments: [],
        events: [],
        allowedRoles: [],
      },
      mohSetup,
      appointments: [],
      counts: {
        facilities: 0,
        clinicians: 0,
        members: hydratedMembers.length,
        services: 0,
        appointments: null,
        patients: 0,
        rating: null,
      },
      requiresSetup: [],
    };
  }

  const [
    facilityLinksResult,
    clinicianRosterResult,
    institutionServiceResult,
    institutionPatientResult,
    documentResult,
    memberResult,
    surveillanceResult,
    mohSetup,
  ] = await Promise.all([
    safeList(
      supabase
        .from("institution_facilities")
        .select("*")
        .eq("institution_id", activeInstitution.id)
        .order("created_at", { ascending: false }),
    ),
    safeRpc("medical_institution_clinician_snapshot", {
      target_institution: activeInstitution.id,
    }),
    safeRpc("medical_institution_service_snapshot", {
      target_institution: activeInstitution.id,
      target_facility: null,
      target_service: null,
    }),
    safeRpc("medical_institution_patient_snapshot", {
      target_institution: activeInstitution.id,
      target_relationship: null,
    }),
    safeList(
      supabase
        .from("institution_documents")
        .select("*")
        .eq("institution_id", activeInstitution.id)
        .order("created_at", { ascending: false }),
    ),
    safeList(
      supabase
        .from("institution_members")
        .select("*")
        .eq("institution_id", activeInstitution.id)
        .order("created_at", { ascending: false }),
      { allowAccessDenied: true },
    ),
    safeRpc("regional_health_institution_surveillance_snapshot", {
      target_institution: activeInstitution.id,
    }).catch((error) => ({ data: null, denied: isAccessDenied(error), error })),
    readMoHExploreSetupForInstitution(activeInstitution.id),
  ]);

  if (facilityLinksResult.missing) setup.push("Facility links");
  if (clinicianRosterResult.missing) setup.push("Clinician roster");
  if (institutionServiceResult.missing) setup.push("Institution services");
  if (institutionPatientResult.missing) setup.push("Institution patients");
  if (documentResult.missing) setup.push("Governance documents");
  if (surveillanceResult.missing) setup.push("Surveillance authority management");

  const facilityLinks = facilityLinksResult.rows;
  const memberUserIds = unique(memberResult.rows.map((row) => row?.user_id));
  let memberProfiles = [];
  if (memberUserIds.length) {
    try {
      const result = await safeList(
        supabase.from("profiles").select("user_id,avatar_url").in("user_id", memberUserIds),
      );
      memberProfiles = result.rows;
    } catch {
      // The row remains usable with initials when a shared profile image is not readable.
      memberProfiles = [];
    }
  }
  const memberProfileByUserId = new Map(memberProfiles.map((row) => [row.user_id, row]));
  const hydratedMembers = memberResult.rows.map((member) => {
    const profile = memberProfileByUserId.get(member?.user_id);
    return {
      ...member,
      avatar_url: member?.avatar_url || profile?.avatar_url || "",
    };
  });
  const facilityIds = unique(facilityLinks.map((row) => row?.healthcare_location_id));
  const { rows: facilities } = facilityIds.length
    ? await safeList(supabase.from("healthcare_locations").select("*").in("id", facilityIds))
    : { rows: [] };
  const facilityById = new Map(facilities.map((row) => [row.id, row]));
  const linkedFacilities = facilityLinks.map((link) => ({
    ...link,
    facility: facilityById.get(link.healthcare_location_id) || null,
  }));

  const clinicianSnapshot = await hydrateInstitutionClinicianSnapshot(clinicianRosterResult.data || { clinicians: [], facilities: [] }, activeInstitution.id);
  const linkedClinicians = Array.isArray(clinicianSnapshot?.clinicians) ? clinicianSnapshot.clinicians : [];
  const clinicianFacilities = Array.isArray(clinicianSnapshot?.facilities) ? clinicianSnapshot.facilities : [];
  const institutionServiceSnapshot = institutionServiceResult.data || { services: [], facilities: [], specialties: [] };
  const institutionServices = Array.isArray(institutionServiceSnapshot?.services) ? institutionServiceSnapshot.services : [];
  const serviceFacilities = Array.isArray(institutionServiceSnapshot?.facilities) ? institutionServiceSnapshot.facilities : [];
  const serviceSpecialties = Array.isArray(institutionServiceSnapshot?.specialties) ? institutionServiceSnapshot.specialties : [];
  const institutionPatientSnapshot = institutionPatientResult.data || { patients: [], facilities: [], healthProfileAccess: false };
  const institutionPatients = Array.isArray(institutionPatientSnapshot?.patients) ? institutionPatientSnapshot.patients : [];

  const realCounts = {
    facilities: linkedFacilities.length,
    clinicians: linkedClinicians.length,
    members: hydratedMembers.length,
    services: institutionServices.length,
    appointments: null,
    patients: institutionPatients.length,
    rating: null,
  };

  if (!linkedFacilities.length) setup.push("Linked facilities");
  if (!linkedClinicians.length) setup.push("Linked clinicians");
  // Services, appointments and patient count are distinct product lanes. The
  // Clinicians roster must not hydrate them by probing clinician-owned data.
  if (!documentResult.rows.length) setup.push("MoU / payment / governance documents");

  return {
    status: "ready",
    user,
    userRoles,
    scopedRole,
    memberships: memberRows,
    institutions,
    activeInstitution,
    members: hydratedMembers,
    linkedFacilities,
    linkedClinicians,
    clinicianFacilities,
    services: institutionServices,
    serviceFacilities,
    serviceSpecialties,
    patients: institutionPatients,
    patientFacilities: Array.isArray(institutionPatientSnapshot?.facilities) ? institutionPatientSnapshot.facilities : [],
    patientHealthProfileAccess: institutionPatientSnapshot?.healthProfileAccess === true,
    patientContractAvailable: !institutionPatientResult.missing,
    canManageServices: canManageInstitutionServices({ userRoles, memberships: memberRows, institutionId: activeInstitution.id }),
    documents: documentResult.rows,
    surveillance: surveillanceResult.data || {
      programmes: [],
      facilities: [],
      assignments: [],
      events: [],
      allowedRoles: [],
    },
    mohSetup,
    appointments: [],
    counts: realCounts,
    requiresSetup: unique(setup),
  };
}

function clinicianRpcError(error, operation) {
  const next = new Error(error?.message || `Institution clinician ${operation} failed.`);
  next.code = error?.code || "";
  next.details = error?.details || "";
  next.hint = error?.hint || "";
  next.operation = operation;
  return next;
}

export async function loadInstitutionClinicianSnapshot({ institutionId }) {
  const { data, error } = await supabase.rpc("medical_institution_clinician_snapshot", {
    target_institution: institutionId,
  });
  if (error) throw clinicianRpcError(error, "snapshot");
  return hydrateInstitutionClinicianSnapshot(data || { clinicians: [], facilities: [] }, institutionId);
}

/**
 * Gets ephemeral Global clinician media URLs. The browser receives only the
 * signed display URL; the Edge Function rechecks the existing reader snapshot
 * before it accesses a Global profile or Storage as service role.
 */
export async function loadInstitutionClinicianMediaDisplay({ institutionId, rosterId }) {
  const { data, error } = await supabase.functions.invoke("institution-clinician-cover-display", {
    body: { institutionId, rosterId },
  });
  if (error) throw error;
  return {
    coverDisplayUrl: normalize(data?.coverDisplayUrl),
    avatarDisplayUrl: normalize(data?.avatarDisplayUrl),
  };
}

export async function loadInstitutionClinicianCoverDisplay({ institutionId, rosterId }) {
  const media = await loadInstitutionClinicianMediaDisplay({ institutionId, rosterId });
  return media.coverDisplayUrl;
}

/**
 * Facility covers are Institution-owned media. Unlike facility logos, the
 * browser never receives the storage path: the display function independently
 * verifies the active Institution reader before returning an ephemeral URL.
 */
export async function loadMedicalInstitutionFacilityCoverDisplay({ institutionId, facilityId }) {
  const { data, error } = await supabase.functions.invoke("institution-facility-cover-display", {
    body: { institutionId, facilityId },
  });
  if (error) throw error;
  return {
    coverDisplayUrl: normalize(data?.coverDisplayUrl),
    hasCover: data?.hasCover === true,
  };
}

/**
 * A missing protected function is a release/configuration state, not an upload
 * failure the facility administrator can correct in the browser. Keep the UI
 * honest until the governed media contract is deployed.
 */
export function isMedicalInstitutionFacilityCoverBackendUnavailable(error) {
  const status = Number(error?.context?.status || error?.status || error?.cause?.status || 0);
  return status === 404 || /function.*not found|status code 404|not found/i.test(String(error?.message || ""));
}

export async function createInstitutionClinician({ institutionId, roster }) {
  const { data, error } = await supabase.rpc("medical_institution_clinician_create", {
    target_institution: institutionId,
    roster_input: roster,
  });
  if (error) throw clinicianRpcError(error, "create");
  return data;
}

export async function findInstitutionClinicianExactMatch({ institutionId, email = "", registrationNumber = "" }) {
  const { data, error } = await supabase.rpc("medical_institution_clinician_find_exact_match", {
    target_institution: institutionId,
    email_input: email || null,
    registration_input: registrationNumber || null,
  });
  if (error) throw clinicianRpcError(error, "exact match");
  return data || { state: "no_match", match: null };
}

export async function updateInstitutionClinician({ institutionId, rosterId, roster }) {
  const { data, error } = await supabase.rpc("medical_institution_clinician_update", {
    target_institution: institutionId,
    target_roster: rosterId,
    roster_input: roster,
  });
  if (error) throw clinicianRpcError(error, "update");
  return data;
}

export async function changeInstitutionClinicianStatus({ institutionId, rosterId, status, reason }) {
  const { data, error } = await supabase.rpc("medical_institution_clinician_change_status", {
    target_institution: institutionId,
    target_roster: rosterId,
    status_input: status,
    reason_input: reason,
  });
  if (error) throw clinicianRpcError(error, "status change");
  return data;
}

async function clinicianInvitationRpc(name, args, operation) {
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw clinicianRpcError(error, operation);
  return data || {};
}

export function isInstitutionClinicianInvitationNotification(notification) {
  return /^institution_clinician_invitation:[0-9a-f-]{36}$/i.test(String(notification?.type || "").trim());
}

export function institutionClinicianInvitationIdFromNotification(notification) {
  const match = String(notification?.type || "").trim().match(/^institution_clinician_invitation:([0-9a-f-]{36})$/i);
  return match?.[1] || "";
}

export async function loadInstitutionClinicianInvitationManagerSnapshot({ institutionId }) {
  return clinicianInvitationRpc("medical_institution_clinician_invitation_manager_snapshot", { target_institution: institutionId }, "invitation manager snapshot");
}

export async function sendInstitutionClinicianInvitation({ institutionId, rosterId }) {
  return clinicianInvitationRpc("medical_institution_clinician_invitation_send", { target_institution: institutionId, target_roster: rosterId }, "send clinician invitation");
}

export async function resendInstitutionClinicianInvitation({ institutionId, invitationId }) {
  return clinicianInvitationRpc("medical_institution_clinician_invitation_resend", { target_institution: institutionId, target_invitation: invitationId }, "resend clinician invitation");
}

export async function revokeInstitutionClinicianInvitation({ institutionId, invitationId }) {
  return clinicianInvitationRpc("medical_institution_clinician_invitation_revoke", { target_institution: institutionId, target_invitation: invitationId }, "revoke clinician invitation");
}

export async function loadInstitutionClinicianInvitationReview({ invitationId }) {
  return clinicianInvitationRpc("medical_institution_clinician_invitation_review", { target_invitation: invitationId }, "clinician invitation review");
}

export async function respondToInstitutionClinicianInvitation({ invitationId, decision }) {
  return clinicianInvitationRpc("medical_institution_clinician_invitation_respond", { target_invitation: invitationId, decision_input: decision }, "clinician invitation response");
}

function institutionServiceRpcError(error, operation) {
  const next = new Error(error?.message || `Institution service ${operation} failed.`);
  next.code = error?.code || "";
  next.details = error?.details || "";
  next.hint = error?.hint || "";
  next.operation = operation;
  return next;
}

export async function loadInstitutionServiceSnapshot({ institutionId, facilityId = null, serviceId = null }) {
  const { data, error } = await supabase.rpc("medical_institution_service_snapshot", {
    target_institution: institutionId,
    target_facility: facilityId || null,
    target_service: serviceId || null,
  });
  if (error) throw institutionServiceRpcError(error, "snapshot");
  return data || { services: [], facilities: [], specialties: [] };
}

export async function createInstitutionService({ institutionId, service }) {
  const { data, error } = await supabase.rpc("medical_institution_service_create", {
    target_institution: institutionId,
    service_input: service,
  });
  if (error) throw institutionServiceRpcError(error, "create");
  return data;
}

export async function updateInstitutionService({ institutionId, serviceId, service }) {
  const { data, error } = await supabase.rpc("medical_institution_service_update", {
    target_institution: institutionId,
    target_service: serviceId,
    service_input: service,
  });
  if (error) throw institutionServiceRpcError(error, "update");
  return data;
}

export async function changeInstitutionServiceStatus({ institutionId, serviceId, status }) {
  const { data, error } = await supabase.rpc("medical_institution_service_change_status", {
    target_institution: institutionId,
    target_service: serviceId,
    status_input: status,
  });
  if (error) throw institutionServiceRpcError(error, "status change");
  return data;
}

export async function archiveInstitutionService({ institutionId, serviceId, reason }) {
  const { data, error } = await supabase.rpc("medical_institution_service_archive", {
    target_institution: institutionId,
    target_service: serviceId,
    reason_input: reason,
  });
  if (error) throw institutionServiceRpcError(error, "archive");
  return data;
}

// Delivery authority is deliberately a separate Institution-owned relationship.
// These RPCs never read or write a clinician's personal doctor_services or prices.
export async function loadInstitutionServiceDeliverySnapshot({ institutionId, serviceId }) {
  const { data, error } = await supabase.rpc("medical_institution_service_delivery_snapshot", {
    target_institution: institutionId,
    target_service: serviceId,
  });
  if (error) throw institutionServiceRpcError(error, "delivery snapshot");
  return data || { facilities: [], eligibleClinicians: [], assignments: [] };
}

export async function createInstitutionServiceDeliveryAssignment({ institutionId, serviceId, assignment }) {
  const { data, error } = await supabase.rpc("medical_institution_service_delivery_create_assignment", {
    target_institution: institutionId,
    target_service: serviceId,
    assignment_input: assignment,
  });
  if (error) throw institutionServiceRpcError(error, "delivery assignment create");
  return data;
}

export async function updateInstitutionServiceDeliveryAssignment({ institutionId, serviceId, assignmentId, assignment }) {
  const { data, error } = await supabase.rpc("medical_institution_service_delivery_update_assignment", {
    target_institution: institutionId,
    target_service: serviceId,
    target_assignment: assignmentId,
    assignment_input: assignment,
  });
  if (error) throw institutionServiceRpcError(error, "delivery assignment update");
  return data;
}

export async function archiveInstitutionServiceDeliveryAssignment({ institutionId, serviceId, assignmentId, reason }) {
  const { data, error } = await supabase.rpc("medical_institution_service_delivery_archive_assignment", {
    target_institution: institutionId,
    target_service: serviceId,
    target_assignment: assignmentId,
    reason_input: reason,
  });
  if (error) throw institutionServiceRpcError(error, "delivery assignment archive");
  return data;
}

function institutionPatientRpcError(error, operation) {
  const next = new Error(error?.message || `Institution patient ${operation} failed.`);
  next.code = error?.code || "";
  next.details = error?.details || "";
  next.hint = error?.hint || "";
  next.operation = operation;
  return next;
}

export async function loadInstitutionPatientSnapshot({ institutionId, relationshipId = null }) {
  const { data, error } = await supabase.rpc("medical_institution_patient_snapshot", {
    target_institution: institutionId,
    target_relationship: relationshipId || null,
  });
  if (error) throw institutionPatientRpcError(error, "snapshot");
  return data || { patients: [], facilities: [], healthProfileAccess: false };
}

export async function findInstitutionPatientExactMatch({ institutionId, email = "", phone = "", patientReference = "" }) {
  const { data, error } = await supabase.rpc("medical_institution_patient_find_exact_match_v2", {
    target_institution: institutionId,
    email_input: email || null,
    phone_input: phone || null,
    patient_reference_input: patientReference || null,
  });
  if (error) throw institutionPatientRpcError(error, "exact match");
  return data || { match: null };
}

export async function loadInstitutionPatientProfile({ institutionId, relationshipId }) {
  const { data, error } = await supabase.rpc("medical_institution_patient_profile_snapshot", {
    target_institution: institutionId,
    target_relationship: relationshipId,
  });
  if (error) throw institutionPatientRpcError(error, "profile access");
  return data || { healthProfileAccess: false, identity: {}, healthProfile: {} };
}

export async function createInstitutionPatientRelationship({ institutionId, relationship }) {
  const { data, error } = await supabase.rpc("medical_institution_patient_create", {
    target_institution: institutionId,
    relationship_input: relationship,
  });
  if (error) throw institutionPatientRpcError(error, "create");
  return data;
}

export async function updateInstitutionPatientRelationship({ institutionId, relationshipId, relationship }) {
  const { data, error } = await supabase.rpc("medical_institution_patient_update", {
    target_institution: institutionId,
    target_relationship: relationshipId,
    relationship_input: relationship,
  });
  if (error) throw institutionPatientRpcError(error, "update");
  return data;
}

export async function commitInstitutionPatientCsv({ institutionId, importInput }) {
  const { data, error } = await supabase.rpc("medical_institution_patient_csv_commit", {
    target_institution: institutionId,
    import_input: importInput,
  });
  if (error) throw institutionPatientRpcError(error, "CSV import");
  return data;
}

export async function loadInstitutionPatientCsvResults({ batchId }) {
  const { data, error } = await supabase.rpc("medical_institution_patient_csv_results", { target_batch: batchId });
  if (error) throw institutionPatientRpcError(error, "CSV import results");
  return data;
}

export async function commitInstitutionGovernedCsv({ institutionId, importType, idempotencyKey, rows }) {
  const { data, error } = await supabase.rpc("institution_governed_csv_import_commit", {
    target_institution: institutionId,
    import_type_input: importType,
    idempotency_key_input: idempotencyKey,
    rows_input: rows,
  });
  if (error) throw institutionPatientRpcError(error, "CSV import");
  return data;
}

export async function loadInstitutionGovernedCsvResults({ batchId }) {
  const { data, error } = await supabase.rpc("institution_governed_csv_import_results", { target_batch: batchId });
  if (error) throw institutionPatientRpcError(error, "CSV import results");
  return data;
}

export async function assignInstitutionSurveillanceAuthority({
  institutionId,
  memberId,
  facilityLinkId,
  programmeId,
  role,
  startsAt,
  endsAt,
  reason,
}) {
  const { data, error } = await supabase.rpc("regional_health_institution_assign_authority", {
    target_institution: institutionId,
    target_member: memberId,
    target_institution_facility: facilityLinkId,
    target_program: programmeId || null,
    target_role: role,
    starts_at_input: startsAt || null,
    ends_at_input: endsAt || null,
    reason_input: reason,
  });
  if (error) throw error;
  return data;
}

export async function inviteInstitutionMember({ institutionId, email, role = "member", reason = "" }) {
  const { data, error } = await supabase.rpc("regional_health_institution_invite_member", {
    target_institution: institutionId,
    member_email: email,
    member_role: role,
    reason_input: reason || null,
  });
  if (error) throw error;
  return data;
}

export async function inviteInstitutionScopedMember({ institutionId, email, audience, supervisorMemberId, reason, expiresInDays = 14 }) {
  const { data, error } = await supabase.rpc("institution_surveillance_invite_scoped_member", {
    target_institution: institutionId,
    member_email: email,
    audience_input: audience,
    supervised_by_input: supervisorMemberId,
    reason_input: reason,
    expires_in_days: expiresInDays,
  });
  if (error) throw error;
  return data;
}

export async function acceptInstitutionScopedMemberInvitation({ invitationId }) {
  const { data, error } = await supabase.rpc("institution_surveillance_accept_scoped_member_invitation", {
    target_invitation: invitationId,
  });
  if (error) throw error;
  return data;
}

export async function revokeInstitutionScopedMemberInvitation({ institutionId, invitationId, reason }) {
  const { data, error } = await supabase.rpc("institution_surveillance_revoke_scoped_member_invitation", {
    target_institution: institutionId,
    target_invitation: invitationId,
    reason_input: reason,
  });
  if (error) throw error;
  return data;
}

export async function loadInstitutionScopedInvitationSnapshot({ institutionId }) {
  const { data, error } = await supabase.rpc("institution_surveillance_scoped_invitation_snapshot", {
    target_institution: institutionId,
  });
  if (error) throw error;
  return data || { invitations: [] };
}

export async function createRegionalHealthInstitution({ name, country, email, phone, idempotencyKey, setupProgress = {} }) {
  const { data, error } = await supabase.rpc("create_moh_workspace_for_explore", {
    institution_name: name,
    institution_country: country,
    contact_email: email,
    contact_phone: phone,
    request_key: idempotencyKey,
    setup_payload: setupProgress,
  });
  if (error) throw error;
  return data;
}

export async function loadMoHWorkspaceSetup({ institutionId }) {
  const { data, error } = await supabase.rpc("get_moh_workspace_setup", {
    target_institution: institutionId,
  });
  if (error) throw error;
  return data;
}

// This is the sole client-side interpretation of the governed MoH entry RPC.
// An active membership authorises a workspace; only setup_status === "complete"
// authorises an immediate operational-home landing.
export async function resolveMoHWorkspaceDisposition() {
  const { data, error } = await supabase.rpc("get_moh_workspace_entry_state");
  if (error) throw error;

  const entry = data && typeof data === "object" ? data : {};
  const isMoHEligible = entry?.isMohEligible === true;
  const hasInactiveMembership = entry?.hasInactiveMembership === true;
  const activeInstitutionId = normalize(entry?.activeInstitutionId);
  const isMoHExploreWorkspace = entry?.isMohExploreWorkspace === true;
  const setupStatus = isMoHExploreWorkspace
    ? normalize(entry?.setupStatus).toLowerCase()
    : "";

  return {
    isMoHEligible,
    hasInactiveMembership,
    activeInstitutionId,
    isMoHExploreWorkspace,
    setupStatus,
    shouldResumeOnboarding:
      isMoHEligible &&
      !hasInactiveMembership &&
      (!activeInstitutionId || (isMoHExploreWorkspace && setupStatus !== "complete")),
  };
}

export async function saveMoHWorkspaceSetup({ institutionId, setupProgress, status = "complete_later" }) {
  const { data, error } = await supabase.rpc("save_moh_workspace_setup", {
    target_institution: institutionId,
    setup_payload: setupProgress || {},
    requested_status: status,
  });
  if (error) throw error;
  return data;
}

export async function acceptInstitutionEvaluationReviewerInvite(token = "") {
  const source = String(token || "").trim();
  if (!source) throw new Error("Invitation token is required.");
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(source));
  const hash = Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, "0")).join("");
  const { data, error } = await supabase.rpc("accept_institution_evaluation_reviewer_invite", { invite_token_hash: hash });
  if (error) throw error;
  return data;
}

export async function sendInstitutionEvaluationReviewerInvite({ institutionId, email }) {
  const { data, error } = await supabase.functions.invoke("send-institution-evaluation-invite", {
    body: {
      institution_id: institutionId,
      email,
    },
  });
  if (error) throw error;
  if (!data?.ok) throw new Error(data?.error || "Unable to send the Institution invitation.");
  return data;
}

export function getInstitutionMemberIntakeErrorMessage(error) {
  const message = String(error?.message || "").toLowerCase();
  if (message.includes("institution_member_account_not_found")) {
    return "No AfriMed account was found for that email. The member must complete account setup first.";
  }
  if (message.includes("institution_member_email_unconfirmed")) {
    return "This AfriMed account exists, but its email is not confirmed.";
  }
  if (message.includes("institution_member_account_ineligible")) {
    return "This AfriMed account is not eligible for Institution member linking.";
  }
  if (message.includes("institution_member_membership_conflict")) {
    return "This account has an existing Institution membership conflict. No changes were made.";
  }
  return "Unable to add the existing Institution member. No access or authority was granted.";
}

export async function revokeInstitutionSurveillanceAuthority({ institutionId, assignmentId, reason }) {
  const { data, error } = await supabase.rpc("regional_health_institution_revoke_authority", {
    target_institution: institutionId,
    target_assignment: assignmentId,
    reason_input: reason,
  });
  if (error) throw error;
  return data;
}

export async function updateInstitutionMemberStatus({ institutionId, memberId, status, reason }) {
  const { data, error } = await supabase.rpc("regional_health_institution_update_member_status", {
    target_institution: institutionId,
    target_member: memberId,
    target_status: status,
    reason_input: reason,
  });
  if (error) throw error;
  return data;
}

const FACILITY_IMAGE_BUCKET = "institution-facility-images";
const FACILITY_IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/jpg", "image/webp"]);
const FACILITY_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

function facilityImageExtension(file) {
  const name = String(file?.name || "");
  const extension = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
  if (["png", "jpg", "jpeg", "webp"].includes(extension)) return extension === "jpg" ? "jpeg" : extension;
  return file?.type === "image/png" ? "png" : file?.type === "image/webp" ? "webp" : "jpeg";
}

function assertFacilityImage(file) {
  if (!file) return;
  if (!FACILITY_IMAGE_TYPES.has(String(file.type || "").toLowerCase())) {
    throw new Error("Use a PNG, JPEG, or WebP logo.");
  }
  if (Number(file.size || 0) > FACILITY_IMAGE_MAX_BYTES) {
    throw new Error("The facility logo must be 5MB or smaller.");
  }
}

export async function loadMedicalInstitutionNetwork({ institutionId }) {
  const result = await safeRpc("medical_institution_network_snapshot", { target_institution: institutionId });
  if (result.missing) return { facilities: [], services: [], specialties: [], pendingMigration: true };
  const facilities = Array.isArray(result.data?.facilities) ? result.data.facilities : [];
  const hydratedFacilities = await Promise.all(
    facilities.map(async (facility) => {
      if (!facility?.logoPath) return facility;
      try {
        return { ...facility, logoUrl: await getMedicalInstitutionFacilityLogoUrl(facility.logoPath) };
      } catch {
        return facility;
      }
    }),
  );
  return {
    facilities: hydratedFacilities,
    services: Array.isArray(result.data?.services) ? result.data.services : [],
    specialties: Array.isArray(result.data?.specialties) ? result.data.specialties : [],
  };
}

export async function searchMedicalInstitutionAdminCandidates({ institutionId, query = "" }) {
  const { data, error } = await supabase.rpc("medical_institution_network_search_admin_candidates", {
    target_institution: institutionId,
    search_input: query,
  });
  if (error) throw error;
  return Array.isArray(data?.candidates) ? data.candidates : [];
}

export async function assignMedicalInstitutionFacilityAdmin({ institutionId, facilityId, userId }) {
  const { data, error } = await supabase.rpc("medical_institution_network_ensure_facility_admin", {
    target_institution: institutionId,
    target_facility: facilityId,
    target_user: userId,
    reason_input: "Facility administrator assigned from Medical Institutions",
  });
  if (error) throw error;
  return data;
}

export async function assignMedicalInstitutionFacilityMember({ institutionId, facilityId, userId, role = "admin", customRoleLabel = "" }) {
  const { data, error } = await supabase.rpc("medical_institution_network_ensure_facility_member", {
    target_institution: institutionId,
    target_facility: facilityId,
    target_user: userId,
    role_input: role,
    reason_input: "Facility member assigned from Medical Institutions",
    custom_role_label_input: customRoleLabel || null,
  });
  if (error) throw error;
  return data;
}

export async function createMedicalInstitutionFacilityAdminInvitation({ institutionId, facilityId, fullName, email, phone = "" }) {
  const { data, error } = await supabase.rpc("medical_institution_network_create_facility_admin_invitation", {
    target_institution: institutionId,
    target_facility: facilityId,
    full_name_input: fullName,
    email_input: email,
    phone_input: phone || null,
  });
  if (error) throw error;
  return data;
}

export async function createMedicalInstitutionFacilityMemberInvitation({ institutionId, facilityId, fullName, email, phone = "", role = "admin", customRoleLabel = "" }) {
  const { data, error } = await supabase.rpc("medical_institution_network_create_facility_member_invitation", {
    target_institution: institutionId,
    target_facility: facilityId,
    full_name_input: fullName,
    email_input: email,
    phone_input: phone || null,
    role_input: role,
    custom_role_label_input: customRoleLabel || null,
  });
  if (error) throw error;
  return data;
}

export async function createMedicalInstitutionFacility({ institutionId, facility }) {
  const { data, error } = await supabase.rpc("medical_institution_network_create_facility_with_members", {
    target_institution: institutionId,
    facility_name_input: facility.name,
    facility_type_input: facility.type,
    address_input: facility.address,
    city_input: facility.city,
    postal_code_input: facility.postalCode || null,
    country_input: facility.country,
    phone_input: facility.phone || null,
    email_input: facility.email || null,
    website_input: facility.website || null,
    description_input: facility.description || null,
    opening_hours_input: facility.openingHours,
    service_ids_input: facility.serviceIds || [],
    reference_input: facility.reference || null,
    specialty_ids_input: facility.specialtyIds || [],
    custom_specialty_labels_input: facility.customSpecialties || [],
    additional_members_input: facility.additionalMembers || [],
    pending_member_invitations_input: facility.pendingMemberInvitations || [],
  });
  if (error) throw error;
  return data;
}

export async function updateMedicalInstitutionFacility({ institutionId, facilityId, facility }) {
  const { data, error } = await supabase.rpc("medical_institution_network_update_facility", {
    target_institution: institutionId,
    target_facility: facilityId,
    facility_name_input: facility.name,
    facility_type_input: facility.type,
    address_input: facility.address,
    city_input: facility.city,
    postal_code_input: facility.postalCode || null,
    country_input: facility.country,
    website_input: facility.website || null,
    opening_hours_input: facility.openingHours,
    registration_tax_id_input: facility.registrationTaxId || null,
    accessibility_input: facility.accessibility || "private",
    description_input: facility.description || null,
    specialty_ids_input: facility.specialtyIds || [],
    custom_specialty_labels_input: facility.customSpecialties || [],
  });
  if (error) throw error;
  return data;
}

export async function uploadMedicalInstitutionFacilityLogo({ institutionId, facilityId, file }) {
  assertFacilityImage(file);
  if (!file) return null;
  const extension = facilityImageExtension(file);
  const path = `${institutionId}/${facilityId}/logo-${Date.now()}.${extension}`;
  const { error: uploadError } = await supabase.storage.from(FACILITY_IMAGE_BUCKET).upload(path, file, {
    cacheControl: "3600",
    upsert: false,
  });
  if (uploadError) throw uploadError;

  const { data, error } = await supabase.rpc("medical_institution_network_set_facility_logo", {
    target_institution: institutionId,
    target_facility: facilityId,
    storage_path_input: path,
  });
  if (error) {
    await supabase.storage.from(FACILITY_IMAGE_BUCKET).remove([path]);
    throw error;
  }
  return data;
}

/**
 * Clears the existing facility identity logo through the same authorised
 * facility-logo RPC used for setting it.  This deliberately remains distinct
 * from the protected facility-cover contract.
 */
export async function removeMedicalInstitutionFacilityLogo({ institutionId, facilityId, logoPath }) {
  const previousPath = normalize(logoPath);
  const { error } = await supabase.rpc("medical_institution_network_set_facility_logo", {
    target_institution: institutionId,
    target_facility: facilityId,
    storage_path_input: null,
  });
  if (error) throw error;
  if (previousPath) {
    const { error: storageError } = await supabase.storage.from(FACILITY_IMAGE_BUCKET).remove([previousPath]);
    if (storageError) throw storageError;
  }
  return true;
}

export async function uploadMedicalInstitutionFacilityCover({ institutionId, facilityId, file }) {
  assertFacilityImage(file);
  if (!file) return "";
  const body = new FormData();
  body.set("action", "set");
  body.set("institutionId", institutionId);
  body.set("facilityId", facilityId);
  body.set("cover", file);
  const { data, error } = await supabase.functions.invoke("institution-facility-cover-media", { body });
  if (error) throw error;
  return normalize(data?.coverDisplayUrl);
}

export async function removeMedicalInstitutionFacilityCover({ institutionId, facilityId }) {
  const { data, error } = await supabase.functions.invoke("institution-facility-cover-media", {
    body: { action: "clear", institutionId, facilityId },
  });
  if (error) throw error;
  return data?.cleared === true;
}

export async function getMedicalInstitutionFacilityLogoUrl(path) {
  const source = normalize(path);
  if (!source) return "";
  const { data, error } = await supabase.storage.from(FACILITY_IMAGE_BUCKET).createSignedUrl(source, 60 * 15);
  if (error) throw error;
  return data?.signedUrl || "";
}
